import { Injectable, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { LocationSelectors } from '@core/locations-data';
import { SelectLocationsDialogComponent, SelectLocationsSelectMode } from '@core/locations-legacy';
import type { Location } from '@core/locations-types';
import { JobsWrapperDataService } from '@core/offers-data';
import { PlacementActions, PlacementSelectors } from '@core/placements-data';
import { PlacementActionsStrategyService } from '@core/placements-legacy';
import type { Placement } from '@core/placements-types';
import { DialogService, DialogWidth, NotificationsController } from '@core/user-interface-legacy';

@Injectable({ providedIn: 'root' })
export class EditWorkLocationsFacade {
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);
  private readonly actions$ = inject(Actions);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly actionsStrategy = inject(PlacementActionsStrategyService);
  private readonly jobsWrapperDataService = inject(JobsWrapperDataService);

  private locations: Location[] = [];

  constructor() {
    this.store
      .select(LocationSelectors.allEntities)
      .subscribe(locations => (this.locations = locations));
  }

  public editLocations$(
    offerId: number,
    assignedLocationIds: number[] = [],
    dialogOptions?: { heading: string; subheading: string },
  ): Observable<number[]> {
    return this.jobsWrapperDataService.getJobItemById$(offerId).pipe(
      filterBoolean,
      take(1),
      switchMap(offer =>
        this.openSelectLocationsDialog$(
          assignedLocationIds,
          offer.locationIds || [],
          dialogOptions,
        ).afterClosed.pipe(filterBoolean),
      ),
    );
  }

  public editPlacementLocations$(placementId: number): Observable<number[]> {
    return this.store.select(PlacementSelectors.byIdWithReferences(placementId)).pipe(
      filterBoolean,
      take(1),
      switchMap(placement =>
        this.openSelectLocationsDialog$(
          placement.locationIds || [],
          placement.offer!.locationIds || [],
        ).afterClosed.pipe(
          filterBoolean,
          switchMap(assignedLocationIds =>
            this.handleDispatchPlacementLocations(placement.id, assignedLocationIds),
          ),
        ),
      ),
    );
  }

  public canEditWorkLocations(placement: Placement): boolean {
    const placementLocations = placement.locations || [];
    const availableActions = this.actionsStrategy.getAvailable(placement);
    const activeOfferLocations = this.locations.filter(
      location => placement.offer?.locationIds?.includes(location.id) && !location.archivedAt,
    );

    return (
      availableActions.includes('edit_preferred_work_locations') &&
      !!activeOfferLocations.length &&
      !placement.hiredInThisPlacement &&
      !(
        placementLocations.length === 1 &&
        !placementLocations[0].archivedAt &&
        activeOfferLocations.length === 1 &&
        activeOfferLocations[0].id === placementLocations[0].id
      )
    );
  }

  private openSelectLocationsDialog$(
    assignedLocationIds: number[],
    availableLocationIds: number[],
    dialogOptions?: { heading: string; subheading: string },
  ) {
    return this.dialogService.openCustom(
      SelectLocationsDialogComponent,
      { width: DialogWidth.SmallX },
      {
        initialAssignedLocationIds: assignedLocationIds || [],
        selectMode: SelectLocationsSelectMode.Multi,
        availableLocationIds,
        heading:
          dialogOptions?.heading ??
          this.i18n.translate('app.candidate.placements.edit_work_location_dialog.heading'),
        subheading:
          dialogOptions?.subheading ??
          this.i18n.translate('app.candidate.placements.edit_work_location_dialog.subheading'),
      },
    );
  }

  private handleDispatchPlacementLocations(
    placementId: number,
    locationIds: number[],
  ): Observable<number[]> {
    this.store.dispatch(
      PlacementActions.changePreferredWorkLocations({
        payload: {
          placementId,
          locationIds,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(PlacementActions.changePreferredWorkLocationsSuccess),
      take(1),
      tap(() =>
        this.notificationsCtrl.success(
          this.i18n.translate('app.candidate.placements.edit_work_location_dialog.change_success'),
        ),
      ),
      map(() => locationIds),
    );
  }
}
